import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import {
  getWorkOrder,
  updateWorkOrder,
  aiSummarize,
  listUsers,
  listParts,
  addPartToWO,
  uploadFile,
  workOrderPdfUrl,
  workOrderDocxUrl,
  API,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, TypePill, PriorityPill } from "@/components/Pills";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Sparkles, MapPin, Building2, Wrench, Calendar, Package, FileDown, Navigation, FileText, Printer } from "lucide-react";
import { toast } from "sonner";
import PhotosPanel from "@/components/PhotosPanel";

export default function WorkOrderDetail() {
  const { id } = useParams();
  const { user, settings } = useAuth();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [techs, setTechs] = useState([]);
  const [parts, setParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState("");
  const [qty, setQty] = useState(1);
  const [aiBusy, setAiBusy] = useState(false);
  const [summary, setSummary] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const d = await getWorkOrder(id);
    setData(d);
  };
  useEffect(() => {
    load();
    listUsers().then((u) => setTechs(u.filter((x) => x.role === "technician")));
    listParts().then(setParts);
  }, [id]);

  if (!data) {
    return (
      <Layout title="Loading…">
        <div className="h-40 flex items-center justify-center">
          <div className="h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const w = data.work_order;
  const canEdit = user?.role === "admin" || (user?.role === "technician" && w.assignee_id === user.user_id) || user?.role === "technician";

  const changeStatus = async (status) => {
    await updateWorkOrder(w.id, { status });
    toast.success(`Status → ${status}`);
    load();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await updateWorkOrder(w.id, { note });
    setNote("");
    toast.success("Note added");
    load();
  };

  const assign = async (assignee_id) => {
    const tech = techs.find((t) => t.user_id === assignee_id);
    await updateWorkOrder(w.id, { assignee_id, assignee_name: tech?.name });
    toast.success("Assigned");
    load();
  };

  const runSummary = async () => {
    setAiBusy(true);
    try {
      const r = await aiSummarize(w.id);
      setSummary(r.summary);
    } catch {
      toast.error("AI failed");
    } finally {
      setAiBusy(false);
    }
  };

  const handleUpload = async (e, kind = "before", group = "Main") => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(`${group}__${kind}`);
    try {
      const up = await uploadFile(file);
      const photoUrl = up.url.startsWith("http")
        ? up.url
        : `${API.replace(/\/api$/, "")}${up.url}`;
      await updateWorkOrder(w.id, {
        photo_url: photoUrl,
        photo_kind: kind,
        photo_group: group,
      });
      toast.success(`${kind === "before" ? "Before" : "After"} photo added to "${group}"`);
      await load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const consumePart = async () => {
    if (!selectedPart || qty < 1) {
      toast.error("Select a part and quantity");
      return;
    }
    try {
      await addPartToWO(w.id, selectedPart, Number(qty));
      toast.success("Part consumed");
      setSelectedPart("");
      setQty(1);
      const [d, p] = await Promise.all([getWorkOrder(id), listParts()]);
      setData(d);
      setParts(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout
      title={
        <span className="flex items-center gap-3">
          <span className="font-mono">{w.number}</span>
          <span className="text-zinc-300">·</span>
          <span className="truncate">{w.title}</span>
        </span>
      }
      breadcrumbs={["Work Orders", w.number]}
    >
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/work-orders"
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-orange-600"
        >
          <ArrowLeft className="h-3 w-3" /> Back
        </Link>
        <div className="flex gap-2">
          <a
            data-testid="wo-pdf-btn"
            href={workOrderPdfUrl(w.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-50 text-xs font-bold uppercase tracking-wider"
          >
            <FileDown className="h-3.5 w-3.5" /> PDF
          </a>
          <a
            data-testid="wo-docx-btn"
            href={workOrderDocxUrl(w.id)}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-50 text-xs font-bold uppercase tracking-wider"
          >
            <FileText className="h-3.5 w-3.5" /> Word
          </a>
          <button
            data-testid="wo-print-btn"
            onClick={() => window.open(workOrderPdfUrl(w.id), "_blank")}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-sm border border-orange-400 bg-orange-50 hover:bg-orange-100 text-xs font-bold uppercase tracking-wider text-orange-800"
          >
            <Printer className="h-3.5 w-3.5" /> Print
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="engineered-card p-5">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <TypePill type={w.type} />
              <PriorityPill priority={w.priority} />
              <StatusPill status={w.status} />
              <span className="ml-auto text-xs font-mono text-zinc-500">
                {new Date(w.created_at).toLocaleString()}
              </span>
            </div>
            <h2 className="font-display font-bold text-2xl text-zinc-950 mb-2">{w.title}</h2>
            <p className="text-zinc-700 whitespace-pre-line leading-relaxed">{w.description || "No description."}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-5 border-t border-zinc-200">
              <Meta icon={Building2} label="Client" value={data.client?.name} />
              <SiteMeta site={data.site} />
              <Meta icon={Wrench} label="Asset" value={data.asset ? `${data.asset.tag} · ${data.asset.name}` : "—"} />
              <Meta icon={Calendar} label="Due" value={w.due_date ? new Date(w.due_date).toLocaleString() : "—"} />
            </div>
          </div>

          {/* Photos · Before & After */}
          <PhotosPanel
            photos={w.photos || []}
            captions={w.photo_group_captions || {}}
            canEdit={canEdit}
            uploading={uploading}
            onUpload={handleUpload}
            woId={w.id}
            aiEnabled={settings?.ai_enabled}
            onChanged={load}
          />

          {/* Parts used */}
          <div className="engineered-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Package className="h-4 w-4 text-orange-500" />
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
                Parts Used
              </div>
            </div>
            {(w.parts_used || []).length === 0 ? (
              <div className="text-sm text-zinc-500 mb-3">No parts consumed.</div>
            ) : (
              <div className="space-y-1 mb-3">
                {(w.parts_used || []).map((p, i) => (
                  <div
                    key={`${p.part_id || p.sku}-${p.at || i}`}
                    data-testid={`wo-part-${p.sku}`}
                    className="flex items-center justify-between text-sm border-b border-zinc-100 py-1.5"
                  >
                    <div>
                      <span className="font-mono text-xs font-bold mr-2">{p.sku}</span>
                      <span>{p.name}</span>
                    </div>
                    <div className="font-mono text-xs text-zinc-600">
                      ×{p.quantity} · ${(p.unit_cost * p.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {canEdit && (
              <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-zinc-200">
                <Select value={selectedPart} onValueChange={setSelectedPart}>
                  <SelectTrigger data-testid="wo-part-select" className="rounded-sm h-10 flex-1">
                    <SelectValue placeholder="Select part…" />
                  </SelectTrigger>
                  <SelectContent>
                    {parts
                      .filter((p) => p.stock > 0)
                      .map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.sku} · {p.name} ({p.stock} {p.unit})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="wo-part-qty"
                  type="number"
                  min="1"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  className="rounded-sm h-10 w-full sm:w-24"
                />
                <Button
                  data-testid="wo-part-add"
                  size="sm"
                  onClick={consumePart}
                  className="rounded-sm bg-orange-500 hover:bg-orange-600 h-10"
                >
                  Add
                </Button>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="engineered-card p-5">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">
              Activity & Notes
            </div>
            <div className="space-y-3 max-h-72 overflow-y-auto pr-2 mb-3">
              {(w.notes || []).length === 0 && (
                <div className="text-sm text-zinc-500">No notes yet.</div>
              )}
              {(w.notes || []).map((n, i) => (
                <div
                  key={`${n.at || ""}-${n.by_id || i}`}
                  className="border-l-2 border-orange-500 pl-3"
                >
                  <div className="text-xs text-zinc-500 font-mono">
                    {new Date(n.at).toLocaleString()} · <b>{n.by}</b>
                  </div>
                  <div className="text-sm text-zinc-800 mt-0.5 whitespace-pre-line">{n.text}</div>
                </div>
              ))}
            </div>
            {canEdit && (
              <div className="border-t border-zinc-200 pt-3">
                <Textarea
                  data-testid="wo-note"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note from the field…"
                  className="rounded-sm mb-2"
                />
                <Button
                  size="sm"
                  data-testid="wo-add-note"
                  onClick={addNote}
                  className="rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white"
                >
                  Add note
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="engineered-card p-5">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3">
              Actions
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                data-testid="wo-status-in_progress"
                variant="outline"
                onClick={() => changeStatus("in_progress")}
                className="rounded-sm justify-center"
              >
                Start
              </Button>
              <Button
                data-testid="wo-status-on_hold"
                variant="outline"
                onClick={() => changeStatus("on_hold")}
                className="rounded-sm justify-center"
              >
                Hold
              </Button>
              <Button
                data-testid="wo-status-completed"
                onClick={() => changeStatus("completed")}
                className="rounded-sm justify-center bg-emerald-600 hover:bg-emerald-700 col-span-2 text-white"
              >
                Mark Complete
              </Button>
            </div>
          </div>

          <div className="engineered-card p-5">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3">
              Assignment
            </div>
            {user?.role === "admin" ? (
              <Select value={w.assignee_id || ""} onValueChange={assign}>
                <SelectTrigger data-testid="wo-assignee" className="rounded-sm h-10">
                  <SelectValue placeholder="Assign technician" />
                </SelectTrigger>
                <SelectContent>
                  {techs.map((t) => (
                    <SelectItem key={t.user_id} value={t.user_id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-sm">
                {w.assignee_name || <span className="text-zinc-500">Unassigned</span>}
              </div>
            )}
          </div>

          {settings?.ai_enabled && (
            <div className="engineered-card p-5 border-orange-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 font-bold text-orange-700 text-sm">
                  <Sparkles className="h-4 w-4" /> AI Summary
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="ai-summary-btn"
                  onClick={runSummary}
                  disabled={aiBusy}
                  className="rounded-sm border-orange-400 text-orange-800 hover:bg-orange-50"
                >
                  {aiBusy ? "…" : "Generate"}
                </Button>
              </div>
              {summary ? (
                <pre className="text-xs whitespace-pre-wrap font-mono text-zinc-800 leading-relaxed" data-testid="ai-summary-output">
                  {summary}
                </pre>
              ) : (
                <div className="text-xs text-zinc-600">Generate a manager-ready summary of this work order.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* End-of-WO Export Bar */}
      <div className="engineered-card mt-6 p-5" data-testid="wo-export-bar">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
              Generate Report
            </div>
            <h3 className="font-display font-bold text-lg text-zinc-950">
              Print, PDF or Word
            </h3>
            <p className="text-xs text-zinc-600 mt-1">
              A4 layout · enlarged Before/After photos · excludes parts inventory.
            </p>
          </div>
          <div className="flex gap-2">
            <a
              data-testid="wo-pdf-btn-bottom"
              href={workOrderPdfUrl(w.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 h-10 px-4 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-50 text-xs font-bold uppercase tracking-wider"
            >
              <FileDown className="h-4 w-4" /> Download PDF
            </a>
            <a
              data-testid="wo-docx-btn-bottom"
              href={workOrderDocxUrl(w.id)}
              className="inline-flex items-center gap-1 h-10 px-4 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-50 text-xs font-bold uppercase tracking-wider"
            >
              <FileText className="h-4 w-4" /> Download Word
            </a>
            <button
              data-testid="wo-print-btn-bottom"
              onClick={() => window.open(workOrderPdfUrl(w.id), "_blank")}
              className="inline-flex items-center gap-1 h-10 px-4 rounded-sm bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold uppercase tracking-wider"
            >
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const Meta = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1 text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500">
      <Icon className="h-3 w-3" /> {label}
    </div>
    <div className="text-sm text-zinc-900 mt-1 font-semibold">{value || "—"}</div>
  </div>
);

const SiteMeta = ({ site }) => {
  if (!site) return <Meta icon={MapPin} label="Site" value="—" />;
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500">
        <MapPin className="h-3 w-3" /> Site
      </div>
      <div className="text-sm text-zinc-900 mt-1 font-semibold">{site.name}</div>
      {site.map_url && (
        <a
          data-testid="wo-site-map-link"
          href={site.map_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase text-orange-700 hover:text-orange-800"
        >
          <Navigation className="h-3 w-3" /> Open in Maps
        </a>
      )}
    </div>
  );
};
